<template>
  <div class="my-10">
    <h1 class="font-bold text-2xl">{{ $t('page.edit') }}</h1>
    <div v-if="originLoaded" class="mt-5 bg-white rounded p-5">
      <div class="grid grid-cols-3 gap-4 max-md:grid-cols-2 max-sm:grid-cols-1">
        <TextField
          v-if="categoryDetail?.has_name"
          ref="title"
          :defaultSingleValue="newPublication?.name"
          :label="$t('page.name')"
          :required="true"
          name="name"
        ></TextField>
        <TextField
          ref="phone"
          :defaultSingleValue="newPublication?.phone"
          :label="$t('form.phone')"
          :required="true"
          name="phone"
          placeholder="+993"
        ></TextField>
        <NumberField
          v-if="categoryDetail?.has_price"
          ref="price"
          :defaultSingleValue="newPublication?.price"
          :label="$t('page.price')"
          :required="!!categoryDetail?.is_price_required"
          name="price"
        ></NumberField>
        <template v-for="field in ogProperties" :key="field.id">
          <component
            :is="getComponent(field.data_type)"
            :ref="field.id"
            :field="field"
            :label="field.name"
            :name="field.name"
            :options="field.values"
            :property="field.id"
            :defaultValues="newPublication?.select_values"
            :defaultSelectValue="newPublication?.select_values?.find(el => el.property == field.id)"
            :defaultSingleValue="getSanitizedValue(field)"
            :defaultParentMultiValues="
              newPublication?.select_values?.filter(el => el.property == field.id)
            "
            :defaultChildrenValues="
              (() => {
                const filteredParents =
                  newPublication?.select_values?.filter(el => el.property === field.id) || [];
                const parentIds = filteredParents.map(parent => parent.id);
                return (
                  newPublication?.select_values?.filter(value =>
                    parentIds.includes(value.parent?.id),
                  ) || []
                );
              })()
            "
            :required="field.is_required"
          >
          </component>
        </template>

        <LocationNestedSelectField
          v-if="publicationLocations && categoryDetail?.has_address"
          ref="address"
          :defaultChildValue="newPublication?.district_id"
          :defaultParentValue="newPublication?.location_id"
          :label="$t('form.address')"
          :options="publicationLocations"
          name="district"
        ></LocationNestedSelectField>
      </div>
      <div class="flex flex-col mt-4">
        <div class="flex flex-col gap-2">
          <label class="font-semibold" for="message"
            >{{ $t('page.info') }}<span class="text-red-500">*</span></label
          >
          <Field
            id="description"
            v-model="formData.description"
            :maxlength="$store?.setting?.max_publication_description_length"
            as="textarea"
            class="w-full rounded border px-8 py-5 placeholder-[#5F5F5F] resize-none max-md:py-4"
            cols="30"
            name="description"
            rows="6"
          >
          </Field>
          <ErrorMessage class="text-red-500" name="description"></ErrorMessage>
        </div>

        <!-- THUMBNAIL PICTURE (строго отдельно от фото/видео) -->
        <div v-if="categoryDetail?.has_thumbnail_picture" class="w-full flex flex-col gap-2 mt-4">
          <p class="font-semibold">
            {{ $t('page.thumbnailPicture') }}
            <span class="text-red-500" v-show="categoryDetail?.is_thumbnail_picture_required"
              >*</span
            >
          </p>

          <div class="flex items-center gap-4">
            <input
              ref="thumbnailInput"
              class="hidden"
              type="file"
              accept=".jpg,.jpeg,.png"
              @change="onThumbnailChange"
            />

            <button
              type="button"
              class="flex gap-3 items-center bg-ml-secondary text-main-color rounded-md cursor-pointer py-3 px-5 hover:bg-ml-main active:bg-ml-main"
              @click="$refs.thumbnailInput.click()"
              :disabled="loadingThumbnail"
            >
              <IconUpload />
              {{ $t('page.uploadImage') }}
            </button>

            <InlineLoader class="!w-8 !h-8" :showLoader="loadingThumbnail"></InlineLoader>

            <button
              v-if="thumbnailId"
              type="button"
              class="bg-[#EB5757] text-white px-4 py-2 rounded hover:opacity-90"
              @click="removeThumbnail"
            >
              {{ $t('page.remove') }}
            </button>
          </div>

          <div v-if="thumbnailPreview" class="mt-2">
            <img
              :src="thumbnailPreview"
              class="h-28 w-48 object-contain rounded border"
              alt="thumb-preview"
            />
          </div>
        </div>

        <template v-if="categoryDetail?.has_image">
          <div class="w-full flex flex-col gap-2 mt-4">
            <p for="additionalinfo" class="font-semibold">
              {{ $t('page.uploadImage') }} ({{ imageFiles.length }}/{{ getMaxImageCount }})
              <span class="text-red-500" v-show="categoryDetail?.is_image_required">*</span>
            </p>
            <div
              @dragover="dragoverImage"
              @dragleave="dragleaveImage"
              @drop="dropImage"
              class="flex justify-center items-center flex-col border-4 border-dashed border-ml-input-100 p-20"
              :class="{ 'bg-ml-input-200': isDraggingImage }"
            >
              <input
                :disabled="imageFiles.length >= getMaxImageCount"
                ref="imageFile"
                name="files"
                class="hidden"
                type="file"
                multiple
                id="imageFileUpload"
                @change="onImageFileChange"
                accept=".jpg,.jpeg,.png"
              />
              <InlineLoader class="!w-8 !h-8" :showLoader="loadingImage"></InlineLoader>
              <label
                v-if="!loadingImage"
                class="flex gap-3 items-center bg-ml-secondary text-main-color rounded-md cursor-pointer py-3 px-5 hover:bg-ml-main active:bg-ml-main"
                for="imageFileUpload"
              >
                <template v-if="imageFiles.length < getMaxImageCount">
                  <IconUpload></IconUpload>{{ $t('page.uploadImage') }}
                </template>
                <template v-else>
                  <p class="text-blue-500">
                    {{ $t('page.maxImage') }}
                  </p>
                </template>
              </label>
            </div>
          </div>
        </template>
        <div
          class="grid grid-cols-3 gap-5 mt-4 max-lg:grid-cols-2 max-md:grid-cols-1"
          v-if="imageFiles.length"
        >
          <div
            v-for="(file, idx) in imageFiles"
            :key="(file.detail?.name || file.detail?.id || file.path) + '-' + idx"
            class="w-full flex items-center justify-between p-2 gap-4 border-t border-b"
          >
            <img :src="file.path" alt="uploaded-image" class="h-20 w-32 object-fit" />
            <div class="flex items-center gap-2">
              <input
                type="radio"
                class="scale-150"
                name="main-image"
                :id="(file.detail?.name || file.detail?.id || file.path || idx) + '-' + idx"
              />
              <label :for="(file.detail?.name || file.detail?.id || file.path || idx) + '-' + idx">
                {{ file.detail?.name || file.detail?.id || 'file' }}
              </label>
            </div>
            <button
              class="ml-3 mt-1 bg-[#EB5757] p-2.5 rounded"
              type="button"
              @click="removeImage(idx)"
              title="Remove file"
            >
              <IconTrash></IconTrash>
            </button>
          </div>
        </div>
        <div class="w-full flex flex-col gap-2 mt-4">
          <p for="additionalinfo" class="font-semibold">
            {{ $t('page.uploadVideo') }} ({{ videoFiles.length }}/{{ getMaxVideoCount }})
          </p>
          <div
            @dragover="dragoverVideo"
            @dragleave="dragleaveVideo"
            @drop="dropVideo"
            class="flex justify-center items-center flex-col border-4 border-dashed border-ml-input-100 p-20"
            :class="{ 'bg-ml-input-200': isDraggingVideo }"
          >
            <input
              :disabled="videoFiles.length >= getMaxVideoCount"
              ref="videoFile"
              name="files"
              class="hidden"
              type="file"
              multiple
              id="videoFileUpload"
              @change="onVideoFileChange"
              accept=".mp4,.mov,.avi,.mkv,.webm"
            />
            <InlineLoader class="!w-8 !h-8" :showLoader="loadingVideo"></InlineLoader>
            <label
              v-if="!loadingVideo"
              class="flex gap-3 items-center bg-ml-secondary text-main-color rounded-md cursor-pointer py-3 px-5 hover:bg-ml-main active:bg-ml-main"
              for="videoFileUpload"
            >
              <template v-if="videoFiles.length < getMaxVideoCount">
                <IconUpload></IconUpload>{{ $t('page.uploadVideo') }}
              </template>
              <template v-else>
                <p class="text-blue-500">
                  {{ $t('page.maxVideo') }}
                </p>
              </template>
            </label>
          </div>
        </div>

        <div
          class="grid grid-cols-3 gap-5 mt-4 max-lg:grid-cols-2 max-md:grid-cols-1"
          v-if="videoFiles.length"
        >
          <div
            v-for="(file, idx) in videoFiles"
            :key="(file.detail?.name || file.detail?.id || file.path) + '-' + idx"
            class="w-full flex items-center justify-between p-2 gap-4 border-t border-b"
          >
            <IconMedia class="w-20 h-20"></IconMedia>
            <div class="flex items-center gap-2">
              <!-- <input type="radio" class="scale-150" name="main-image" :id="file.detail.name"> -->
              <label>
                {{ file.detail?.name || file.detail?.id || 'file' }}
              </label>
            </div>
            <button
              class="ml-3 mt-1 bg-[#EB5757] p-2.5 rounded"
              type="button"
              @click="removeVideo(idx)"
              title="Remove file"
            >
              <IconTrash></IconTrash>
            </button>
          </div>
        </div>
      </div>
      <div class="my-4">
        <div class="grid grid-cols-3 my-6">
          <SwitchField
            v-if="categoryDetail?.on_credit"
            ref="on_credit"
            :defaultSingleValue="newPublication?.on_credit"
            :label="$t('page.onCredit')"
            name="on_credit"
          />

          <SwitchField
            v-if="categoryDetail?.on_exchange"
            ref="on_exchange"
            :defaultSingleValue="newPublication?.on_exchange"
            :label="$t('page.onExchange')"
            name="on_exchange"
          />

          <SwitchField
            ref="allow_comments"
            :defaultSingleValue="newPublication?.allow_comments"
            :label="$t('page.allowComments')"
            name="allow_comments"
          />
        </div>
      </div>
      <div>
        <SubmitBtn @click="onSubmitForm">{{ $t('page.addEdit') }}</SubmitBtn>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/api';
import TextField from '@cmp/TextField.vue';
import NumberField from '@cmp/NumberField.vue';
import SwitchField from '@cmp/SwitchField.vue';
import SelectFieldEdit from '@cmp/SelectFieldEdit.vue';
import MultiSelectFieldEdit from '@cmp/MultiSelectFieldEdit.vue';
import NestedSelectFieldEdit from '@cmp/NestedSelectFieldEdit.vue';
import LocationNestedSelectField from '@cmp/LocationNestedSelectField.vue';
import MultiNestedSelectFieldEdit from '@cmp/MultiNestedSelectFieldEdit.vue';
import NestedMultiSelectFieldEdit from '@cmp/NestedMultiSelectFieldEdit.vue';
import Multiselect from '@vueform/multiselect';
import IconUpload from '@icons/IconUpload.vue';
import IconTrash from '@icons/IconTrash.vue';
import IconMedia from '@icons/IconMedia.vue';

import InlineLoader from '@cmp/InlineLoader.vue';
import { ErrorMessage, Field, Form, useField } from 'vee-validate';
import * as yup from 'yup';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { i18n } from '@/i18n';
import { isVideo } from '@/helpers';
import { getNestedSelectValues } from '@/helpers/nestedSelectCache';

export default {
  name: 'editMyPublication',

  components: {
    TextField,
    SwitchField,
    MultiSelectFieldEdit,
    MultiNestedSelectFieldEdit,
    SelectFieldEdit,
    NumberField,
    Multiselect,
    NestedSelectFieldEdit,
    LocationNestedSelectField,
    Field,
    Form,
    ErrorMessage,
    IconUpload,
    InlineLoader,
    IconTrash,
    IconMedia,
    NestedMultiSelectFieldEdit,
  },
  setup() {
    const { t } = useI18n();
    const childComponents = ref([]);
    const on_credit = ref();
    const on_exchange = ref();
    const allow_comments = ref();
    const price = ref();
    const phone = ref();
    const title = ref();
    const address = ref();

    return {
      childComponents,
      on_credit,
      on_exchange,
      allow_comments,
      price,
      phone,
      title,
      address,
    };
  },
  data() {
    return {
      initialPayloadHash: null,
      initialHashReady: false,
      thumbnailFile: null,
      thumbnailPreview: null,
      thumbnailId: null,
      loadingThumbnail: false,
      originLoaded: false,
      originId: null,
      originPublication: {},
      newPublication: {},
      loading: false,
      loadingImage: false,
      loadingVideo: false,
      currentCategory: null,
      ogProperties: [],
      properties: [],
      publicationLocations: [],
      isDraggingVideo: false,
      isDraggingImage: false,
      imageFiles: [],
      videoFiles: [],
      imageFileUrls: [],
      videoFileUrls: [],
      imageFileIds: [],
      videoFileIds: [],

      categoryDetail: {},

      groups: {
        mode: 'tags',
        value: [],
        options: [],
        searchable: true,
        createTag: true,
      },
      formData: {
        category: null,
        origin: 0,
        district: 0,
        on_exchange: false,
        on_credit: false,
        description: '',
        allow_comments: false,
        price: 0,
        name: '',
        phone: '',
        images: [],
        values: [],
        select_values: [],
      },
    };
  },
  methods: {
    isVideo,
    async validateDynamicFields() {
      var isValid = [];
      var fields = [];
      const state = { valid: true };

      for (let index = 0; index < this.childComponents.length; index++) {
        const element = this.childComponents[index];
        const refEl = Array.isArray(this.$refs[element])
          ? this.$refs[element][0]
          : this.$refs[element];
        let x = await refEl.validateField();
        isValid.push(x.valid);
        fields.push(x);
      }
      if (isValid.includes(false)) {
        state['valid'] = false;
      }
      state['fields'] = fields;
      return state;
    },

    normalizeBool(v) {
      if (v === true || v === false) return v;
      if (v === 1 || v === '1') return true;
      if (v === 0 || v === '0') return false;

      if (typeof v === 'string') {
        const s = v.trim().toLowerCase();
        if (['true', 'yes', 'on'].includes(s)) return true;
        if (['false', 'no', 'off', ''].includes(s)) return false;
      }

      return !!v;
    },

    normalizeId(v) {
      if (v === undefined || v === null) return null;
      if (typeof v === 'object') return v.id ?? v.value ?? null;
      return v;
    },

    hashPayload(payload) {
      // делаем стабильный hash: сортировки, чистка, чтобы сравнение было корректным
      const uniq = arr => Array.from(new Set(arr));

      const normValues = (payload.values || [])
        .map(x => ({
          property: Number(x.property),
          name: String(x.name ?? '').trim(),
        }))
        .filter(x => x.property && x.name !== '')
        .sort((a, b) => a.property - b.property);

      const normSelect = uniq(
        (payload.select_values || [])
          .map(v => this.normalizeId(v))
          .filter(v => v !== null && v !== '' && v !== undefined)
          .map(v => Number(v)),
      ).sort((a, b) => a - b);

      const normImages = uniq((payload.images || []).map(Number)).sort((a, b) => a - b);

      const stable = {
        category: Number(payload.category ?? 0),
        origin: Number(payload.origin ?? 0),
        district: Number(payload.district ?? 0),

        name: String(payload.name ?? '').trim(),
        phone: String(payload.phone ?? '').trim(),
        price: payload.price === null || payload.price === undefined ? null : Number(payload.price),

        description: String(payload.description ?? '').trim(),

        on_credit: this.normalizeBool(payload.on_credit),
        on_exchange: this.normalizeBool(payload.on_exchange),
        allow_comments: this.normalizeBool(payload.allow_comments),

        thumbnail_picture: payload.thumbnail_picture ? Number(payload.thumbnail_picture) : null,

        values: normValues,
        select_values: normSelect,
        images: normImages,
      };

      return JSON.stringify(stable);
    },

    async captureInitialHash() {
      // Важно: вызываем после того как ref-компоненты уже отрисованы
      await this.$nextTick();
      await this.$nextTick();

      // Собираем "как при сабмите": читаем текущие значения через validateField()
      // (обычно это не показывает ошибок, потому что дефолты валидные)
      const payload = {
        category: this.newPublication?.category_id,
        origin: this.originId,
        district: 0,

        name: this.newPublication?.name ?? '',
        phone: this.newPublication?.phone ?? '',
        price: this.newPublication?.price ?? null,

        description: this.formData.description ?? '',
        on_credit: this.categoryDetail?.on_credit
          ? (this.newPublication?.on_credit ?? false)
          : false,
        on_exchange: this.categoryDetail?.on_exchange
          ? (this.newPublication?.on_exchange ?? false)
          : false,
        allow_comments: this.newPublication?.allow_comments ?? false,

        thumbnail_picture: this.categoryDetail?.has_thumbnail_picture ? this.thumbnailId : null,

        values: [],
        select_values: [],
        images: [...this.imageFileIds, ...this.videoFileIds],
      };

      // Адрес
      if (this.categoryDetail?.has_address && this.$refs.address?.validateField) {
        const address = await this.$refs.address.validateField();
        payload.district = address?.value?.[1] ?? 0;
      } else {
        payload.district = this.newPublication?.district_id ?? 0;
      }

      // Верхние поля (title/phone/price/switches)
      if (this.categoryDetail?.has_name && this.$refs.title?.validateField) {
        const title = await this.$refs.title.validateField();
        payload.name = title?.value ?? payload.name;
      }
      if (this.$refs.phone?.validateField) {
        const phone = await this.$refs.phone.validateField();
        payload.phone = phone?.value ?? payload.phone;
      }
      if (this.categoryDetail?.has_price && this.$refs.price?.validateField) {
        const p = await this.$refs.price.validateField();
        const pv = p?.value;
        payload.price = pv === '' || pv === undefined ? null : pv;
      }
      if (this.$refs.allow_comments?.validateField) {
        const allow = await this.$refs.allow_comments.validateField();
        payload.allow_comments = this.normalizeBool(allow?.value ?? payload.allow_comments);
      }
      if (this.categoryDetail?.on_exchange && this.$refs.on_exchange?.validateField) {
        const ex = await this.$refs.on_exchange.validateField();
        payload.on_exchange = this.normalizeBool(ex?.value ?? payload.on_exchange);
      }
      if (this.categoryDetail?.on_credit && this.$refs.on_credit?.validateField) {
        const cr = await this.$refs.on_credit.validateField();
        payload.on_credit = this.normalizeBool(cr?.value ?? payload.on_credit);
      }

      // Динамические поля
      const dynamic = await this.validateDynamicFields();
      if (dynamic?.fields?.length) {
        for (const element of dynamic.fields) {
          const raw = element?.value;
          const normalized = raw && typeof raw === 'object' && 'value' in raw ? raw.value : raw;

          if (element.mode === 'single') {
            const v = normalized;
            if (v !== undefined && v !== null && String(v).trim() !== '') {
              payload.values.push({ name: v, property: element.property });
            }
          } else {
            if (Array.isArray(normalized)) payload.select_values.push(...normalized);
            else if (
              normalized !== undefined &&
              normalized !== null &&
              String(normalized).trim() !== ''
            )
              payload.select_values.push(normalized);
          }
        }
      }

      this.initialPayloadHash = this.hashPayload(payload);
      this.initialHashReady = true;
      // console.log('✅ initialPayloadHash captured');
    },

    getSanitizedValue(field) {
      const raw = this.newPublication?.attributes?.[field.name];

      // STR (TextField)
      if (field.data_type === 0) {
        // null/undefined -> ''
        if (raw === null || raw === undefined) return '';
        // любое другое -> строка
        return String(raw);
      }

      // Number
      if ([1, 2].includes(field.data_type)) {
        if (raw === null || raw === undefined || raw === '') return null;
        const value = parseFloat(String(raw).replace(/[^\d.]/g, ''));
        return isNaN(value) ? null : value;
      }

      // остальное
      if (raw === null || raw === undefined) return null;
      return raw;
    },

    async dropImage(e) {
      e.preventDefault();
      await this.onImageFileChange(e.dataTransfer.files);
      this.isDraggingImage = false;
    },
    removeImage(i) {
      const f = this.imageFiles[i];
      if (f?.path?.startsWith('blob:')) URL.revokeObjectURL(f.path);
      this.imageFiles.splice(i, 1);
      this.imageFileIds.splice(i, 1);
    },
    dragoverImage(e) {
      e.preventDefault();
      this.isDraggingImage = true;
    },
    dragleaveImage() {
      this.isDraggingImage = false;
    },

    async onThumbnailChange(e) {
      const file = e.target.files?.[0];
      if (!file) return;

      // Освобождаем старый preview
      if (this.thumbnailPreview) {
        URL.revokeObjectURL(this.thumbnailPreview);
      }

      this.thumbnailFile = file;
      this.thumbnailPreview = URL.createObjectURL(file);
      this.thumbnailId = null;

      try {
        this.loadingThumbnail = true;

        const fd = new FormData();
        fd.append('path', file);

        const res = await api.post('files/medias/', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        if (res && res.status === 201) {
          this.thumbnailId = res.data.id;

          // Чтобы тот же файл можно было выбрать снова
          if (this.$refs.thumbnailInput) this.$refs.thumbnailInput.value = '';
        } else {
          this.$toast.warning(`${i18n.global.t('warning.UPLOAD_FAILED')}`);
          this.thumbnailPreview = null;
          this.thumbnailFile = null;
        }
      } catch (err) {
        console.log(err);
        this.$toast.warning(`${i18n.global.t('warning.UPLOAD_FAILED')}`);
        this.thumbnailPreview = null;
        this.thumbnailFile = null;
      } finally {
        this.loadingThumbnail = false;
      }
    },

    removeThumbnail() {
      if (this.thumbnailPreview) {
        URL.revokeObjectURL(this.thumbnailPreview);
      }

      this.thumbnailId = null;
      this.thumbnailFile = null;
      this.thumbnailPreview = null;

      if (this.$refs.thumbnailInput) {
        this.$refs.thumbnailInput.value = '';
      }
    },

    async onImageFileChange(eOrFiles) {
      const files = eOrFiles?.target?.files || eOrFiles || this.$refs.imageFile?.files || [];

      const remaining = this.getMaxImageCount - this.imageFiles.length;

      if (!files || !files.length) return;

      if (remaining <= 0) {
        this.$toast.info(`${this.$t('page.maxImage')} (${this.getMaxImageCount})`);
        if (eOrFiles?.target && this.$refs.imageFile) this.$refs.imageFile.value = '';
        return;
      }

      const slice = Array.from(files).slice(0, remaining);

      if (slice.length < files.length) {
        this.$toast.info(`${this.$t('page.maxImage')} (${this.getMaxImageCount})`);
      }

      this.loadingImage = true;
      try {
        for (const f of slice) {
          const fd = new FormData();
          fd.append('path', f);

          const res = await api.post('files/medias/', fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });

          if (res?.status === 201) {
            this.imageFiles.push({
              detail: f,
              path: URL.createObjectURL(f),
              isUploaded: true,
            });
            this.imageFileIds.push(res.data.id);
          } else {
            this.$toast.warning(i18n.global.t('warning.UPLOAD_FAILED'));
          }
        }
      } catch (e) {
        console.log(e);
        this.$toast.warning(i18n.global.t('warning.UPLOAD_FAILED'));
      } finally {
        this.loadingImage = false;
        if (eOrFiles?.target && this.$refs.imageFile) this.$refs.imageFile.value = '';
      }
    },
    dragoverVideo(e) {
      e.preventDefault();
      this.isDraggingVideo = true;
    },
    dragleaveVideo() {
      this.isDraggingVideo = false;
    },
    async dropVideo(e) {
      e.preventDefault();
      await this.onVideoFileChange(e.dataTransfer.files);
      this.isDraggingVideo = false;
    },
    removeVideo(i) {
      const f = this.videoFiles[i];
      if (f?.path && typeof f.path === 'string' && f.path.startsWith('blob:')) {
        URL.revokeObjectURL(f.path);
      }
      this.videoFiles.splice(i, 1);
      this.videoFileIds.splice(i, 1);
    },

    async onVideoFileChange(eOrFiles) {
      const files = eOrFiles?.target?.files || eOrFiles || this.$refs.videoFile?.files || [];

      const remaining = this.getMaxVideoCount - this.videoFiles.length;

      if (!files || !files.length) return;

      if (remaining <= 0) {
        this.$toast.info(`${this.$t('page.maxVideo')} (${this.getMaxVideoCount})`);
        if (eOrFiles?.target && this.$refs.videoFile) this.$refs.videoFile.value = '';
        return;
      }

      const slice = Array.from(files).slice(0, remaining);

      if (slice.length < files.length) {
        this.$toast.info(`${this.$t('page.maxVideo')} (${this.getMaxVideoCount})`);
      }

      this.loadingVideo = true;
      try {
        for (const f of slice) {
          const fd = new FormData();
          fd.append('path', f);

          const res = await api.post('files/medias/', fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });

          if (res?.status === 201) {
            this.videoFiles.push({
              detail: f,
              path: URL.createObjectURL(f),
              isUploaded: true,
            });
            this.videoFileIds.push(res.data.id);
          } else {
            this.$toast.warning(i18n.global.t('warning.UPLOAD_FAILED'));
          }
        }
      } catch (e) {
        console.log(e);
        this.$toast.warning(i18n.global.t('warning.UPLOAD_FAILED'));
      } finally {
        this.loadingVideo = false;
        if (eOrFiles?.target && this.$refs.videoFile) this.$refs.videoFile.value = '';
      }
    },

    async getProperties(id) {
      this.loading = true;
      try {
        const response = await api.get(`attributes/properties/?publication_categories=${id}`);
        this.ogProperties = response.data;
        console.log(
          'ogProperties ids:',
          this.ogProperties.map(p => ({ id: p.id, name: p.name, type: p.data_type })),
        );
        console.log('count:', this.ogProperties.length);

        // чтобы не дергать сеть лишний раз — грузим values строго по типам
        await Promise.all(
          this.ogProperties.map(async element => {
            this.childComponents.push(element.id);

            // nested / multi-nested / nested-multi
            if ([6, 7, 8].includes(element.data_type)) {
              element.values = await getNestedSelectValues(element.id); // ✅ кэш + сверка 7 дней
              return;
            }

            // остальные select-типы можно оставить как есть (или тоже позже закэшировать)
            if ([4, 5].includes(element.data_type)) {
              element.values = await this.getSelectValues(element.id); // как было
              return;
            }
          }),
        );
        console.log('childComponents count:', this.childComponents.length);
        console.log('childComponents:', this.childComponents);
        await this.captureInitialHash();
      } catch (e) {
        console.log(e);
      }
      this.loading = false;
    },

    async getSelectValues(property) {
      try {
        const response = await api.get(
          `attributes/select_values/?property=${property}&include_children=true`,
        );
        const data = response.data;
        return Array.isArray(data) ? data : data.results || [];
      } catch (e) {
        console.log(e);
        return [];
      }
    },

    async getPublicationLocations() {
      this.loading = true;
      try {
        const response = await api.get(`publications/locations/`);
        this.publicationLocations = response.data;

        this.publicationLocations.forEach(element => {
          // if (element.data_type >= 4) {
          this.getDistricts(element.id).then(elem => {
            element['values'] = elem;
          });
          // }
        });
      } catch (e) {
        console.log(e);
      }
      this.loading = false;
    },

    async getDistricts(location) {
      this.loading = true;
      try {
        const response = await api.get(`publications/districts/?location=${location}`);
        return response.data;
      } catch (e) {
        console.log(e);
      }
      this.loading = false;
    },

    async onSubmitForm() {
      // console.log(this.$refs)
      this.formData.values = [];
      this.formData.select_values = [];
      let address, price, on_exchange, on_credit, title;
      const isValid = [];
      const dynamicFields = await this.validateDynamicFields();
      if (this.categoryDetail?.has_address) {
        address = await this.$refs.address.validateField();
        isValid.push(address.valid);
      }
      if (this.categoryDetail?.has_name) {
        title = await this.$refs.title.validateField();
        isValid.push(title.valid);
      }
      if (this.categoryDetail?.has_price) {
        price = await this.$refs.price.validateField();
        isValid.push(price.valid);
      }
      if (this.categoryDetail?.on_exchange) {
        on_exchange = await this.$refs.on_exchange.validateField();
        isValid.push(on_exchange.valid);
      }
      if (this.categoryDetail?.on_credit) {
        on_credit = await this.$refs.on_credit.validateField();
        isValid.push(on_credit.valid);
      }
      const phone = await this.$refs.phone.validateField();
      const allow_comments = await this.$refs.allow_comments.validateField();
      console.log('allow_comments raw:', allow_comments.value, typeof allow_comments.value);
      console.log('on_exchange raw:', on_exchange?.value, typeof on_exchange?.value);
      console.log('on_credit raw:', on_credit?.value, typeof on_credit?.value);

      console.log('[EDIT] normalizeBool results:', {
        allow_comments_norm: this.normalizeBool(allow_comments.value),
        on_exchange_norm: this.categoryDetail?.on_exchange
          ? this.normalizeBool(on_exchange?.value)
          : null,
        on_credit_norm: this.categoryDetail?.on_credit
          ? this.normalizeBool(on_credit?.value)
          : null,
      });

      isValid.push(phone.valid, allow_comments.valid);
      // ✅ THUMBNAIL REQUIRED — только если категория разрешает thumbnail picture И он required
      const thumbnailRequired =
        !!this.categoryDetail?.has_thumbnail_picture &&
        !!this.categoryDetail?.is_thumbnail_picture_required;

      if (thumbnailRequired && !this.thumbnailId) {
        this.$toast.error(i18n.global.t('warning.THUMBNAIL_REQUIRED'));
        return;
      }
      if (dynamicFields.valid && !isValid.includes(false)) {
        this.loading = true;
        const fields = dynamicFields.fields;

        for (let index = 0; index < fields.length; index++) {
          const element = fields[index];
          const raw = element?.value;
          const normalized = raw && typeof raw === 'object' && 'value' in raw ? raw.value : raw;

          if (element.mode === 'single') {
            const v = normalized;
            if (v !== undefined && v !== null && String(v).trim() !== '') {
              this.formData.values.push({
                name: String(v).trim(),
                property: Number(element.property),
              });
            }
            continue;
          }

          if (Array.isArray(normalized)) {
            this.formData.select_values.push(...normalized);
          } else {
            if (
              normalized !== undefined &&
              normalized !== null &&
              String(normalized).trim() !== ''
            ) {
              this.formData.select_values.push(normalized);
            }
          }
        }

        // ✅ ВОТ ЭТО ДОБАВЬ СРАЗУ ЗДЕСЬ (после цикла, перед try)
        this.formData.select_values = Array.from(
          new Set(
            this.formData.select_values
              .map(v => this.normalizeId(v)) // если внезапно прилетит объект
              .map(v => Number(v))
              .filter(Number.isFinite), // ✅ строго числа (без NaN/Infinity)
          ),
        );
        try {
          if (this.categoryDetail?.has_name) {
            this.formData.name = title.value; // Присваиваем только если категория требует имени
          }
          this.formData.phone = phone.value;
          if (this.categoryDetail?.has_price) {
            const pv = price?.value;
            this.formData.price = pv === '' || pv === undefined ? null : pv;
          }
          this.formData.allow_comments = this.normalizeBool(allow_comments.value);

          if (this.categoryDetail?.on_exchange) {
            this.formData.on_exchange = this.normalizeBool(on_exchange.value);
          }
          if (this.categoryDetail?.on_credit) {
            this.formData.on_credit = this.normalizeBool(on_credit.value);
          }

          if (!this.categoryDetail?.on_exchange) this.formData.on_exchange = false;
          if (!this.categoryDetail?.on_credit) this.formData.on_credit = false;

          this.formData.origin = this.originId;
          if (this.categoryDetail?.has_address) {
            this.formData.district = address.value[1]; // Присваиваем адрес только если категория требует адрес
          }
          this.formData.category = this.newPublication.category_id;
          // ✅ (2) images — ТОЛЬКО фото+видео, без thumbnail
          this.formData.images = [...this.imageFileIds, ...this.videoFileIds];

          // ✅ (3) thumbnail_picture — отдельно
          this.formData.thumbnail_picture = this.categoryDetail?.has_thumbnail_picture
            ? this.thumbnailId
            : null;

          // ✅ normalize base fields even if inputs are hidden
          if (!this.categoryDetail?.has_name) {
            this.formData.name = this.newPublication?.name ?? '';
          }
          if (!this.categoryDetail?.has_price) {
            this.formData.price = this.newPublication?.price ?? null;
          }

          // ✅ STOP if nothing changed
          const currentHash = this.hashPayload({
            category: this.formData.category,
            origin: this.formData.origin,
            district: this.formData.district,
            name: this.formData.name,
            phone: this.formData.phone,
            price: this.formData.price,
            description: this.formData.description,
            on_credit: this.formData.on_credit,
            on_exchange: this.formData.on_exchange,
            allow_comments: this.formData.allow_comments,
            thumbnail_picture: this.formData.thumbnail_picture,
            values: this.formData.values,
            select_values: this.formData.select_values,
            images: this.formData.images,
          });

          // если вдруг initial hash ещё не успел создаться (редко), создаём и блокируем отправку
          if (!this.initialPayloadHash) {
            this.initialPayloadHash = currentHash;
            this.initialHashReady = true;
            this.loading = false;
            this.$toast.info(i18n.global.t('warning.NOTHING_CHANGED') || 'Вы ничего не изменили');
            return;
          }

          if (currentHash === this.initialPayloadHash) {
            this.loading = false;
            this.$toast.info(i18n.global.t('warning.NOTHING_CHANGED') || 'Вы ничего не изменили');
            return;
          }
          console.log('[EDIT] FINAL formData flags before POST:', {
            allow_comments: this.formData.allow_comments,
            on_exchange: this.formData.on_exchange,
            on_credit: this.formData.on_credit,
          });
          const response = await api.post(`publications/my-publication-requests/`, this.formData);

          this.initialPayloadHash = currentHash;
          this.initialHashReady = true;

          // ✅ 200 = применили сразу к Publication (мимо модерации)
          if (response.status === 200) {
            // ❌ НИКАКОГО варнинга
            this.$toast.success(i18n.global.t('success.SAVED') || 'Сохранено');
            // ✅ редирект в мои объявления
            this.$router.push({ name: 'myPublications' });
            return;
          }

          // ✅ 201 = создали PublicationRequest (в модерацию)
          if (response.status === 201) {
            // ✅ показываем варнинг ТОЛЬКО когда ушло в модерацию
            this.$toast.info(i18n.global.t('warning.PUBLICATION_EDITED'));
            // ✅ редирект в requests
            this.$router.push({ name: 'publicationRequests' });
            return;
          }

          // на всякий случай (не должно быть)
          this.$toast.success(i18n.global.t('success.SAVED') || 'Сохранено');
          this.$router.push({ name: 'myPublications' });
        } catch (e) {
          console.log(e);
        }
        this.loading = false;
      }

      // console.log('isValid', isValid)
      // if (res.includes(false)){
      //     console.log('AINT VALID THIS FORM')
      // }
    },
    async getMyPublication() {
      try {
        const response = await api.get(`publications/my-publications/${this.originId}/`);
        this.originPublication = response.data;
        this.newPublication = response.data;

        // ✅ если это бизнес-объявление — этот экран не должен его редактировать
        const businessId =
          this.newPublication?.business_account?.id ||
          this.newPublication?.business_account_id ||
          null;

        if (businessId) {
          // ВАЖНО: используем replace, чтобы "назад" не возвращал на неправильный edit
          this.$router.replace({ path: `/profile/edit-business-publication/${this.originId}` });
          return;
        }

        console.log('[EDIT] API newPublication flags:', {
          on_credit: this.newPublication?.on_credit,
          on_exchange: this.newPublication?.on_exchange,
          allow_comments: this.newPublication?.allow_comments,
        });

        const grouped = (this.newPublication.select_values || []).reduce((acc, x) => {
          const propId = x.property;
          acc[propId] = acc[propId] || [];
          acc[propId].push({ id: x.id, name: x.name, parent: x.parent?.id });
          return acc;
        }, {});
        console.log('select_values grouped by property:', grouped);

        console.log(
          'publication select_values:',
          this.newPublication.select_values.map(x => ({
            id: x.id,
            name: x.name,
            prop: x.property,
          })),
        );
        console.log('select_values count:', this.newPublication.select_values.length);
        console.log('attributes keys:', Object.keys(this.newPublication.attributes || {}));

        // если у публикации уже есть thumbnail_picture — показываем и сохраняем id
        const tp = this.newPublication?.thumbnail_picture;
        if (tp?.id) {
          this.thumbnailId = tp.id;
          // preview лучше брать из this.newPublication.thumbnail (URL), он уже строкой
          this.thumbnailPreview = this.newPublication?.thumbnail || tp?.path || null;
        }
        this.formData.description = this.newPublication.description;

        this.imageFiles = [];
        this.videoFiles = [];
        this.imageFileIds = [];
        this.videoFileIds = [];
        this.newPublication.images.forEach(e => {
          if (this.isVideo(e.path)) {
            this.videoFileIds.push(e.id);
            this.videoFiles.push({ detail: { name: e.id }, path: e.path, isUploaded: true });
          } else {
            this.imageFileIds.push(e.id);
            this.imageFiles.push({ detail: { name: e.id }, path: e.path, isUploaded: true });
          }
        });
        await this.getCategoryDetail();

        console.log('[EDIT] categoryDetail switches:', {
          on_credit_enabled: this.categoryDetail?.on_credit,
          on_exchange_enabled: this.categoryDetail?.on_exchange,
        });

        console.log('[EDIT] BEFORE originLoaded=true flags:', {
          originLoaded: this.originLoaded,
          on_credit: this.newPublication?.on_credit,
          on_exchange: this.newPublication?.on_exchange,
          allow_comments: this.newPublication?.allow_comments,
          categoryDetail_on_credit: this.categoryDetail?.on_credit,
          categoryDetail_on_exchange: this.categoryDetail?.on_exchange,
        });

        this.originLoaded = true;

        console.log('[EDIT] AFTER originLoaded=true flags:', {
          originLoaded: this.originLoaded,
          on_credit: this.newPublication?.on_credit,
          on_exchange: this.newPublication?.on_exchange,
          allow_comments: this.newPublication?.allow_comments,
        });

        // ⚠️ важно: SwitchField появится только ПОСЛЕ render
        await this.$nextTick();
        await this.$nextTick();

        console.log('[EDIT] NEXTTICK after render refs:', {
          on_credit_ref: this.$refs.on_credit,
          on_exchange_ref: this.$refs.on_exchange,
          allow_comments_ref: this.$refs.allow_comments,
        });

        const allow = this.$refs.allow_comments?.validateField
          ? await this.$refs.allow_comments.validateField()
          : null;

        const ex = this.$refs.on_exchange?.validateField
          ? await this.$refs.on_exchange.validateField()
          : null;

        const cr = this.$refs.on_credit?.validateField
          ? await this.$refs.on_credit.validateField()
          : null;

        console.log('[EDIT] SWITCH validateField values after render:', {
          allow_comments: allow?.value,
          on_exchange: ex?.value,
          on_credit: cr?.value,
        });

        this.getProperties(this.originPublication.category_id);
      } catch (e) {
        console.log(e);
      }
    },
    async getCategoryDetail() {
      try {
        const response = await api.get(
          `publications/categories/${this.originPublication.category_id}/`,
        );
        this.categoryDetail = response.data;
      } catch (e) {
        console.log(e);
      }
    },

    getComponent(type) {
      switch (type) {
        case 0:
          return TextField;
        case 1:
          return NumberField;
        case 2:
          return NumberField;
        case 3:
          return SwitchField;
        case 4:
          return SelectFieldEdit;
        case 5:
          return MultiSelectFieldEdit;
        case 6:
          return NestedSelectFieldEdit;
        case 7:
          return MultiNestedSelectFieldEdit;
        case 8:
          return NestedMultiSelectFieldEdit;
        default:
          return TextField; // Default to text input component
      }
    },
  },
  computed: {
    getMaxImageCount() {
      return this.$store.setting.max_publication_image_count;
    },
    getMaxVideoCount() {
      return this.$store.setting.max_publication_video_count;
    },
  },
  created() {
    this.originId = this.$route.params.id;
    this.getPublicationLocations();
    this.getMyPublication();
  },
};
</script>

<style>
@import '@vueform/multiselect/themes/tailwind.css';
</style>
